"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Icon } from "@/lib/icons/Icon";
import { Field, FormStatus, Input, Select } from "@/components/ui/Form";
import { Badge } from "@/components/ui/Badge/Badge";
import { fetchProjectTasks, type UserProject, type UserTask } from "@/lib/api/c-crm-project-management-user";
import { TaskForm } from "./TaskForm";
import { TaskDetail } from "./TaskDetail";
import { navigateToPluginRoute, pluginRouteHref } from "./taskRoute";
import styles from "./CCrmProjectManagement.module.css";

const PAGE_SIZE = 20;

const SCOPE_ALL = "all";
const SCOPE_OWN = "own";
type ScopeFilter = typeof SCOPE_ALL | typeof SCOPE_OWN;

const TYPE_ALL = "all";

/* Filtr stanu zadania. Domyslnie OTWARTE - lista ma pokazywac biezaca prace,
   a zamkniete zgloszenia sa historia, ktora zaslania to, co wymaga uwagi.
   Ten sam podzial co w gridzie panelu administracyjnego. */
const STATE_OPEN = "open";
const STATE_CLOSED = "closed";
const STATE_ALL = "all";
type StateFilter = typeof STATE_OPEN | typeof STATE_CLOSED | typeof STATE_ALL;

/**
 * Panel zadań pojedynczego projektu (plugin c_crm_project_management).
 *
 * Widok drill-down z listy projektów - NIE jest osobną zakładką sidebara, bo zadania
 * zawsze dotyczą konkretnego projektu (`w ramach projektu`). Dwa tryby przełączane
 * lokalnym stanem, wzorzec z pluginu ogłoszeń (`CAdvertisementsView`: lista <-> formularz):
 *   - `list` - grid zadań widocznych dla klienta,
 *   - `add`  - formularz zgłoszenia.
 *
 * **Zakres listy jest węższy niż dostęp do projektu.** Backend zwraca wyłącznie
 * zadania, które klient zgłosił, obserwuje albo w których zostawił wpis
 * (`IMODEL_M_CRM_TASKS::getClientVisibilityCondition`) - wewnętrzne prace zespołu
 * nie są tu widoczne i pusta lista NIE oznacza, że w projekcie nic się nie dzieje.
 *
 * **Grid:** TanStack Table v8, cały bounded zbiór pobrany raz, sort/filtr/paginacja
 * client-side - identycznie jak listy projektów i usług.
 */

/** Y-m-d H:i:s -> Y-m-d (data bez czasu). Defensywnie zwraca wejście gdy brak spacji. */
function formatDate(raw: string): string {
  if (!raw) return "";
  return raw.split(" ")[0] || raw;
}

/** Etykieta typu - nazwa ze słownika, a gdy jej brak, sam numer (bez udawania że typu nie ma). */
function typeLabel(t: UserTask): string {
  if (t.type_name) return t.type_name;
  return t.id_type > 0 ? `Typ ${t.id_type}` : "-";
}

/** Etykieta priorytetu - jak wyżej, nazwa ze słownika albo sam numer. */
function priorityLabel(t: UserTask): string {
  if (t.priority_name) return t.priority_name;
  return t.id_priority > 0 ? `Priorytet ${t.id_priority}` : "-";
}

/**
 * Czy priorytet jest podwyższony (Wysoki / Pilny / Natychmiastowy).
 * Stałe `M_CRM_TASKS_PRIORITY_*` z define.php: HIGH=3, INSTANT=4, URGENT=5.
 */
function isHighPriority(t: UserTask): boolean {
  return t.id_priority >= 3;
}

export function ProjectTasksPanel({
  projectId,
  view,
  taskId,
}: {
  projectId: number;
  /** Widok z adresu URL - zob. [taskRoute.ts](./taskRoute.ts). */
  view: "tasks" | "add" | "detail";
  /** ID zadania dla widoku szczegółów; null dla pozostałych. */
  taskId: number | null;
}) {
  /* Nagłówek panelu bierzemy z odpowiedzi listy zadań, a nie z propsów: przy
     wejściu z linku bezpośredniego front zna wyłącznie ID projektu z adresu. */
  const [project, setProject] = useState<Pick<UserProject, "id" | "nr" | "name"> | null>(null);
  const [rows, setRows] = useState<UserTask[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>(SCOPE_ALL);
  const [typeFilter, setTypeFilter] = useState<string>(TYPE_ALL);
  const [stateFilter, setStateFilter] = useState<StateFilter>(STATE_OPEN);
  const [sorting, setSorting] = useState<SortingState>([{ id: "add_date", desc: true }]);

  /* Wspólne pobranie listy - wołane przy wejściu w panel ORAZ po udanym zgłoszeniu,
     żeby nowe zadanie pojawiło się od razu. Stan ustawia sama funkcja, bo obu
     wywołań nie da się obsłużyć jednym efektem. */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { project: p, tasks, truncated: tr } = await fetchProjectTasks(projectId);
      setProject(p);
      setRows(tasks);
      setTruncated(tr);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  /* Lista typów do selecta - budowana z faktycznych danych, żeby nie pokazywać
     typów, których w tym projekcie klient w ogóle nie ma. */
  const typeOptions = useMemo(() => {
    const seen = new Map<number, string>();
    rows.forEach((r) => {
      if (!seen.has(r.id_type)) seen.set(r.id_type, typeLabel(r));
    });
    return [...seen.entries()].sort((a, b) => a[0] - b[0]);
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (scopeFilter === SCOPE_OWN && !r.is_own) return false;
      if (typeFilter !== TYPE_ALL && String(r.id_type) !== typeFilter) return false;
      if (stateFilter === STATE_OPEN && r.ended) return false;
      if (stateFilter === STATE_CLOSED && !r.ended) return false;
      return true;
    });
  }, [rows, scopeFilter, typeFilter, stateFilter]);

  const hasActiveFilters =
    scopeFilter !== SCOPE_ALL || typeFilter !== TYPE_ALL || stateFilter !== STATE_OPEN;

  const columns = useMemo(() => {
    const col = createColumnHelper<UserTask>();
    return [
      /* Numer, typ i status w JEDNEJ komorce razem z nazwa - osobne kolumny rozpychaly
         tabele tak, ze na zwyklym ekranie trzeba bylo przewijac ja w poziomie.
         Accessor zwraca komplet tekstow, zeby wyszukiwarka nadal znajdowala po numerze,
         typie i statusie; sortowanie jawnie po samej nazwie. */
      col.accessor((r) => `${r.nr} ${r.name} ${typeLabel(r)} ${r.status_name ?? ""}`, {
        id: "name",
        header: () => "Zadanie",
        sortingFn: (a, b) => a.original.name.localeCompare(b.original.name, "pl"),
        cell: (ctx) => {
          const r = ctx.row.original;
          return (
            <span className={styles.nameCell}>
              <span className={styles.rowMeta}>
                <span className={styles.rowNr}>{r.nr}</span>
                <Badge>{typeLabel(r)}</Badge>
                {r.status_name && <span className={styles.rowStatus}>{r.status_name}</span>}
              </span>
              <span className={styles.name}>{r.name}</span>
              {!r.is_own && <span className={styles.domain}>obserwowane</span>}
            </span>
          );
        },
      }),
      col.accessor((r) => priorityLabel(r), {
        id: "priority",
        header: () => "Priorytet",
        /* Priorytety podwyższone wyróżnione kolorem - klient ma od razu widzieć,
           które z jego zgłoszeń są prowadzone jako pilne. */
        cell: (ctx) => (
          <span className={isHighPriority(ctx.row.original) ? styles.priorityHigh : undefined}>
            {ctx.getValue()}
          </span>
        ),
      }),
      col.accessor((r) => r.assigned_to_fullname ?? "", {
        id: "assigned_to",
        header: () => "Przydzielone do",
        //Pusto = zadanie czeka na przypisanie po naszej stronie; mówimy to wprost.
        cell: (ctx) =>
          ctx.getValue() || <span className={styles.unassigned}>Nieprzydzielone</span>,
      }),
      col.accessor("percent_complete", {
        id: "percent_complete",
        header: () => "Postęp",
        cell: (ctx) => `${ctx.getValue()}%`,
      }),
      col.accessor("add_date", {
        id: "add_date",
        header: () => "Zgłoszono",
        cell: (ctx) => formatDate(ctx.getValue()) || "-",
      }),
      col.display({
        id: "actions",
        header: () => "",
        cell: (ctx) => (
          /* Link, nie przycisk - klient ma móc otworzyć zadanie w nowej karcie
             i skopiować jego adres. Nawigację przejmujemy, żeby nie przeładowywać
             Server Components (hash zmienia sam widok panelu). */
          <a
            className={styles.rowButton}
            href={pluginRouteHref({ view: "detail", projectId, taskId: ctx.row.original.id })}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              navigateToPluginRoute({ view: "detail", projectId, taskId: ctx.row.original.id });
            }}
            title={`Historia i szczegóły zadania ${ctx.row.original.nr}`}
          >
            <Icon name="list-bullets" />
            Historia
          </a>
        ),
      }),
    ];
  }, [projectId]);

  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  const backToTasks = () => navigateToPluginRoute({ view: "tasks", projectId });

  if (view === "detail" && taskId !== null) {
    /* `key` = tożsamość zadania: wejście w inne zadanie montuje nową instancję
       ze świeżym stanem, zamiast resetować go ręcznie w efekcie. */
    return <TaskDetail key={taskId} idTask={taskId} onBack={backToTasks} />;
  }

  if (view === "add") {
    /* Formularz czeka na dane projektu (nagłówek) - przy wejściu z linku
       bezpośredniego lista jeszcze się pobiera. */
    if (!project) {
      return (
        <div className={styles.root}>
          {error ? (
            <FormStatus variant="error">{error}</FormStatus>
          ) : (
            <FormStatus variant="info">Wczytywanie projektu...</FormStatus>
          )}
        </div>
      );
    }

    return (
      <TaskForm
        project={project}
        onCancel={backToTasks}
        onSaved={() => {
          backToTasks();
          //Świeża lista - nowe zgłoszenie ma się pojawić od razu, bez odświeżania strony.
          void load();
        }}
      />
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.panelHeader}>
        <a
          className={styles.backButton}
          href={pluginRouteHref({ view: "projects" })}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            navigateToPluginRoute({ view: "projects" });
          }}
        >
          <Icon name="caret-left" />
          Projekty
        </a>
        <h3 className={styles.panelTitle}>
          {project ? (
            <>
              Zadania - {project.name} <span className={styles.panelNr}>({project.nr})</span>
            </>
          ) : (
            "Zadania projektu"
          )}
        </h3>
        <a
          className={styles.primaryButton}
          href={pluginRouteHref({ view: "add", projectId })}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            navigateToPluginRoute({ view: "add", projectId });
          }}
        >
          <Icon name="plus" />
          Dodaj zadanie
        </a>
      </div>

      <p className={styles.note}>
        Zadania, które zgłosiłeś lub w których uczestniczysz. Wewnętrzne prace zespołu nie są
        tu widoczne.
      </p>

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Field label="Stan" className={styles.filterField}>
            <Select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value as StateFilter)}
            >
              <option value={STATE_OPEN}>Otwarte</option>
              <option value={STATE_CLOSED}>Zamknięte</option>
              <option value={STATE_ALL}>Wszystkie</option>
            </Select>
          </Field>
          <Field label="Zakres" className={styles.filterField}>
            <Select
              value={scopeFilter}
              onChange={(e) => setScopeFilter(e.target.value as ScopeFilter)}
            >
              <option value={SCOPE_ALL}>Wszystkie</option>
              <option value={SCOPE_OWN}>Tylko moje zgłoszenia</option>
            </Select>
          </Field>
          {typeOptions.length > 1 && (
            <Field label="Typ" className={styles.filterField}>
              <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value={TYPE_ALL}>Wszystkie</option>
                {typeOptions.map(([value, label]) => (
                  <option key={value} value={String(value)}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {hasActiveFilters && (
            <button
              type="button"
              className={styles.clearFilters}
              onClick={() => {
                setScopeFilter(SCOPE_ALL);
                setTypeFilter(TYPE_ALL);
                setStateFilter(STATE_OPEN);
              }}
            >
              <Icon name="x" />
              Wyczyść filtry
            </button>
          )}
        </div>
        <div className={styles.searchWrap}>
          <Input
            type="search"
            prefix={<Icon name="magnifying-glass" />}
            placeholder="Szukaj (nr, nazwa, typ, status)..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            aria-label="Szukaj zadań"
          />
        </div>
      </div>

      {loading && <FormStatus variant="info">Wczytywanie zadań...</FormStatus>}
      {error && !loading && <FormStatus variant="error">{error}</FormStatus>}

      {!loading && !error && rows.length === 0 && (
        <FormStatus variant="info">
          Nie masz jeszcze żadnych zgłoszeń w tym projekcie.
        </FormStatus>
      )}

      {!loading && !error && rows.length > 0 && filteredRows.length === 0 && (
        <FormStatus variant="info">Brak zadań spełniających wybrane filtry.</FormStatus>
      )}

      {!loading && !error && filteredRows.length > 0 && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      return (
                        <th
                          key={header.id}
                          className={canSort ? styles.thSortable : undefined}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          aria-sort={
                            sorted === "asc"
                              ? "ascending"
                              : sorted === "desc"
                                ? "descending"
                                : undefined
                          }
                        >
                          <span className={styles.thInner}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <Icon
                                name={sorted === "desc" ? "caret-down" : "caret-up"}
                                className={sorted ? styles.sortIconActive : styles.sortIcon}
                              />
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {truncated && (
            <p className={styles.truncatedHint}>
              Wyświetlono pierwsze 300 zadań. Zawęź wyszukiwanie aby znaleźć pozostałe.
            </p>
          )}

          {pageCount > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <Icon name="caret-left" />
                Poprzednia
              </button>
              <span className={styles.pageInfo}>
                Strona {pageIndex + 1} z {pageCount}
              </span>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Następna
                <Icon name="caret-right" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
